import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { OnboardingStatus } from '../../lib/types';
import { Card, CardBody, Spinner } from '../ui';

const ICON: Record<string, string> = {
  done: '✅',
  pending: '⏳',
  'token-only': '🔑',
  // No es un pendiente: este tenant no tiene sistema que integrar, y el ADR-023
  // dice que eso es una configuración válida, no algo que le falte. Con el
  // reloj de arena al lado, el operador leía «acá falta trabajo» donde no hay.
  no_aplica: '—',
};

interface ChecklistItem {
  testid: string;
  label: string;
  status: 'done' | 'pending' | 'token-only' | 'no_aplica';
}

export function OnboardingChecklist({ tenantId }: { tenantId?: string }) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);

  useEffect(() => {
    if (!tenantId) return;

    let mounted = true;
    const fetchStatus = async () => {
      try {
        const s = await api.tenants.getOnboardingStatus(tenantId);
        if (mounted) setStatus(s);
      } catch {
        // ignorar — el tenant puede no existir todavía si está en step 1
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [tenantId]);

  if (!tenantId) {
    return (
      <Card data-testid="onboarding-checklist" className="sticky top-4">
        <CardBody className="p-5">
          <h3 className="text-xs uppercase font-semibold text-content-tertiary tracking-wider mb-4">
            Progreso de onboarding
          </h3>
          <p className="text-sm text-content-tertiary bg-surface-sunken p-3 rounded border border-edge-subtle">
            Empieza por crear el tenant en el paso 1.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card data-testid="onboarding-checklist" className="sticky top-4">
        <CardBody className="p-5">
          <h3 className="text-xs uppercase font-semibold text-content-tertiary tracking-wider mb-4">
            Progreso de onboarding
          </h3>
          <div className="text-sm text-content-tertiary flex items-center gap-2 bg-surface-sunken p-3 rounded border border-edge-subtle">
            <Spinner size="sm" />
            Cargando…
          </div>
        </CardBody>
      </Card>
    );
  }

  const items: ChecklistItem[] = [
    { testid: 'check-identity', label: 'Identidad', status: status.identity },
    { testid: 'check-telegram', label: 'Telegram', status: status.channels.telegram },
    { testid: 'check-whatsapp', label: 'WhatsApp', status: status.channels.whatsapp },
    { testid: 'check-erp', label: 'ERP', status: status.erp },
    { testid: 'check-flow', label: 'Flow', status: status.flow },
    {
      testid: 'check-kb',
      label: 'Base de Conocimiento',
      status: status.kb === 'synced' ? 'done' : 'pending',
    },
  ];

  return (
    <Card data-testid="onboarding-checklist" className="sticky top-4">
      <CardBody className="p-5">
        <h3 className="text-xs uppercase font-semibold text-content-tertiary tracking-wider mb-4">
          Progreso de onboarding
        </h3>

        <ul className="space-y-3 mb-6">
          {items.map((item) => (
            <li
              key={item.testid}
              data-testid={item.testid}
              className="flex items-center gap-3 text-sm p-2 rounded hover:bg-surface-sunken transition-colors"
            >
              <span className="text-base leading-none">{ICON[item.status]}</span>
              <span
                className={`font-medium ${item.status === 'done' ? 'text-content-primary' : 'text-content-tertiary'}`}
              >
                {item.label}
              </span>
            </li>
          ))}
        </ul>

        <div
          data-testid="ready-indicator"
          className={`mt-4 p-3 rounded-md text-sm font-semibold text-center border shadow-sm transition-colors ${
            status.ready_to_activate
              ? 'border-success-200 text-success-700 bg-success-50 shadow-success-100/50'
              : 'border-edge-subtle text-content-tertiary bg-surface-sunken'
          }`}
        >
          {status.ready_to_activate ? '✅ Listo para activar' : '⏳ Faltan pasos obligatorios'}
        </div>
      </CardBody>
    </Card>
  );
}
